(() => {
  if (customElements.get('ucw-product-reviews')) return;
  const photos = new Map();
  const number = value => new Intl.NumberFormat('nl-NL', {maximumFractionDigits:1}).format(value);
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  class UcwProductReviews extends HTMLElement {
    connectedCallback() {
      this.scope = this.dataset.scope || 'product';
      this.ratings = this.dataset.ratings || 'all';
      this.filterId = ''; this.page = 0; this.loaded = 0; this.loading = false;
      this.controller = new AbortController();
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-review-rating-filter')) {
          if (this.ratings === button.dataset.reviewRatingFilter) return;
          this.ratings = button.dataset.reviewRatingFilter;this.page = 0;this.loaded = 0;
          this.node('[data-review-list]').replaceChildren();this.node('[data-review-more-wrap]').hidden = true;
          this.updateFilters();this.observer?.disconnect();this.load(false);
        } else if (button.hasAttribute('data-review-filter')) {
          if (this.filterId === button.dataset.reviewFilter && this.page) return;
          this.filterId = button.dataset.reviewFilter; this.page = 0; this.loaded = 0;
          this.node('[data-review-list]').replaceChildren();
          this.node('[data-review-more-wrap]').hidden = true;
          this.updateFilters(); this.observer?.disconnect(); this.load(false);
        } else if (button.hasAttribute('data-review-more') && !this.loading) this.load(true);
        else if (button.hasAttribute('data-review-retry')) this.load(this.retryAppend);
      }, {signal:this.controller.signal});
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {this.observer.disconnect();this.load(false);}
        }, {rootMargin:'300px'});
        this.observer.observe(this);
      } else this.load(false);
    }
    disconnectedCallback() {this.controller?.abort();this.request?.abort();this.observer?.disconnect();}
    node(selector) {return this.querySelector(selector);}
    updateFilters() {
      this.querySelectorAll('[data-review-rating-filter]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.reviewRatingFilter === this.ratings))); 
      this.querySelectorAll('[data-review-filter]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.reviewFilter === this.filterId)));
    }
    productUrl(handle) {
      if (!/^[a-z0-9][a-z0-9-]*$/.test(handle)) throw new Error('product');
      return (this.dataset.root || '/') + 'products/' + encodeURIComponent(handle);
    }
    photo(handle, size) {
      const img = el('img');img.width = size;img.height = size;img.alt = '';img.loading = 'lazy';img.decoding = 'async';img.hidden = true;
      const path = this.productUrl(handle);
      if (!photos.has(path)) {
        photos.set(path, fetch(path+'.js',{signal:AbortSignal.timeout(8000),headers:{Accept:'application/json'}})
          .then(response => {if (!response.ok) throw new Error('photo');return response.json();})
          .then(product => {
            if (!product.featured_image) return null;
            const url = new URL(product.featured_image,location.origin);
            if (url.protocol !== 'https:' || ![location.hostname,'cdn.shopify.com'].includes(url.hostname)) return null;
            url.searchParams.set('width','96');return url.href;
          }).catch(() => null));
      }
      photos.get(path).then(src => {if (src) {img.src = src;img.hidden = false;}});
      img.addEventListener('error',() => {img.hidden = true;},{once:true});
      return img;
    }
    filters(products) {
      const host = this.node('[data-review-filters]');
      if (this.filtersBuilt || !['group','collection'].includes(this.scope) || products.length < 2) return;
      this.filtersBuilt = true;
      const common = products[0].title.split(' ');
      while (common.length && !products.every(p => p.title.startsWith(common.join(' ')+' '))) common.pop();
      const prefix = common.length >= 2 ? common.join(' ')+' ' : '';
      const all = el('button');all.type = 'button';all.dataset.reviewFilter = '';
      const allLabel = el('span','ucw-product-reviews__filter-label','Alle producten');
      allLabel.append(el('span','ucw-product-reviews__filter-count',products.reduce((sum,p) => sum+p.count,0)+' beoordelingen'));all.append(allLabel);host.append(all);
      for (const product of products) {
        const button = el('button');button.type = 'button';button.dataset.reviewFilter = product.id;
        button.setAttribute('aria-label',product.title+' · '+product.count+' beoordelingen'+(product.id === this.dataset.productId ? ' · dit product' : ''));
        button.title = product.title;
        let label = product.title.slice(prefix.length).replace(/^huismerk\s+/i,'').replace(/\s*\(.+\)$/,'').trim();
        label = label.charAt(0).toUpperCase()+label.slice(1);
        const copy = el('span','ucw-product-reviews__filter-label',label);
        copy.append(el('span','ucw-product-reviews__filter-count',product.count+' beoordelingen'));
        button.append(this.photo(product.handle,32),copy);host.append(button);
      }
      host.hidden = false;this.updateFilters();
    }
    summary(data) {
      this.node('[data-review-rating]').textContent = number(data.summary.rating);
      this.node('[data-review-stars]').style.setProperty('--ucw-review-fill',Math.max(0,Math.min(100,data.summary.rating*20))+'%');
      this.node('[data-review-total]').textContent = data.summary.count+' beoordelingen';
      const histogram = this.node('[data-review-distribution]');histogram.replaceChildren();
      for (const bucket of data.distribution || []) {
        const row = el('div','ucw-product-reviews__distribution-row');row.setAttribute('aria-label',bucket.rating+' sterren: '+bucket.count+' beoordelingen');
        const bar = el('span','ucw-product-reviews__bar');bar.setAttribute('aria-hidden','true');
        const fill = el('span');fill.style.width = (data.summary.count ? bucket.count/data.summary.count*100 : 0)+'%';bar.append(fill);
        row.append(el('span','',bucket.rating+' ★'),bar,el('span','',String(bucket.count)));histogram.append(row);
      }
      histogram.hidden = !histogram.childNodes.length;
    }
    async collectionProducts(signal) {
      if (this.collectionRefs) return this.collectionRefs;
      const refs = [],seen = new Set();let pages = 1;
      for (let page = 1;page <= pages;page++) {
        const source = new URL(this.dataset.collectionUrl,location.origin);
        if (source.origin !== location.origin) throw new Error('collection');
        source.searchParams.set('view','ucw-review-data');source.searchParams.set('page',String(page));
        const response = await fetch(source,{signal,credentials:'same-origin',headers:{Accept:'text/html'}});
        if (!response.ok) throw new Error('collection');
        const data = await response.json();
        if (data.version !== 1 || data.collectionId !== this.dataset.collectionId || data.page !== page || !Number.isInteger(data.pages) || data.pages < 1 || data.pages > 20 || !Array.isArray(data.products)) throw new Error('collection');
        pages = data.pages;
        for (const product of data.products) {
          if (!/^\d{1,20}$/.test(product.id) || !/^[a-f0-9-]{36}$/.test(product.revision) || seen.has(product.id)) throw new Error('collection');
          seen.add(product.id);refs.push({id:product.id,revision:product.revision});
        }
        if (refs.length > 1000) throw new Error('collection');
      }
      this.collectionRefs = refs;return refs;
    }
    async load(append = false) {
      this.request?.abort();const request = new AbortController();this.request = request;
      const page = append ? this.page+1 : 1;
      const list = this.node('[data-review-list]'),status = this.node('[data-review-status]'),more = this.node('[data-review-more]');
      this.loading = true;this.retryAppend = append;
      list.setAttribute('aria-busy','true');status.textContent = append ? 'Meer reviews worden geladen.' : 'De reviews worden geladen.';
      this.node('[data-review-retry]').hidden = true;more.disabled = true;
      const timeout = setTimeout(() => request.abort(),this.scope === 'collection' ? 20000 : 12000);
      try {
        const url = new URL(this.dataset.endpoint);
        if (url.protocol !== 'https:') throw new Error('endpoint');
        const options = {signal:request.signal,credentials:'omit',headers:{Accept:'application/json'}};
        if (this.scope === 'collection') {
          const products = await this.collectionProducts(request.signal);
          options.method = 'POST';options.headers['Content-Type'] = 'application/json';
          options.body = JSON.stringify({products,page,ratings:this.ratings,filterProductId:this.filterId});
        } else if(this.scope === 'all') {
          url.searchParams.set('page',String(page));
        } else {
          url.searchParams.set('productId',this.dataset.productId);url.searchParams.set('revision',this.dataset.revision);
          url.searchParams.set('scope',this.scope);url.searchParams.set('page',String(page));
          if (this.filterId) url.searchParams.set('filterProductId',this.filterId);
        }
        const response = await fetch(url,options);
        if (!response.ok) throw new Error('unavailable');
        const data = await response.json();
        if (this.request !== request || !this.isConnected) return;
        if (data.version !== 1 || (['collection','all'].includes(this.scope) ? data.scope !== this.scope || (this.collectionRevision && this.collectionRevision !== data.revision) : data.revision !== this.dataset.revision) || !Array.isArray(data.items) || !Number.isInteger(data.pages) || data.pages < 1 || data.page !== page || !Number.isInteger(data.summary?.count)) throw new Error('response');
        const matchingCount = ['collection','all'].includes(this.scope) ? data.matchingCount : data.summary.count;
        if (!Number.isInteger(matchingCount) || matchingCount < 0) throw new Error('response');
        if (['collection','all'].includes(this.scope)) {
          this.collectionRevision = data.revision;
          if (this.scope === 'collection' && !data.products.length) {this.hidden = true;return;}
        }
        const fragment = document.createDocumentFragment();
        for (const item of data.items) fragment.append(this.review(item));
        const firstAdded = fragment.firstElementChild;
        if (append) list.append(fragment); else list.replaceChildren(fragment);
        this.page = page;this.loaded = list.children.length;
        this.filters(data.products || []);this.summary(data);
        status.textContent = matchingCount ? '' : this.scope === 'collection' && this.ratings === 'positive' ? 'Geen beoordelingen met 4 of 5 sterren voor deze selectie. Kies Alle beoordelingen om de overige ervaringen te bekijken.' : 'Er zijn nog geen beoordelingen voor deze selectie.';
        this.node('[data-review-more-wrap]').hidden = !matchingCount;
        this.node('[data-review-progress]').textContent = this.loaded+' van '+matchingCount+' beoordelingen getoond';
        more.hidden = page >= data.pages;
        this.node('[data-review-fallback]').replaceChildren();this.node('[data-review-fallback]').hidden = true;
        if (append && firstAdded) {firstAdded.tabIndex = -1;firstAdded.focus({preventScroll:true});}
      } catch (error) {
        console.warn('UCW reviews:',error.message);
        if (this.request !== request || !this.isConnected) return;
        status.textContent = append ? 'Meer reviews konden niet worden geladen. Probeer het opnieuw.' : 'De reviews kunnen nu niet worden geladen. Probeer het opnieuw.';
        this.node('[data-review-retry]').hidden = false;
        if (!append) {
          const fallback = this.node('[data-review-fallback]');
          if (!fallback.childNodes.length) fallback.append(this.node('[data-review-fallback-template]').content.cloneNode(true));
          fallback.hidden = false;
        }
      } finally {
        clearTimeout(timeout);
        if (this.request === request) {this.loading = false;list.setAttribute('aria-busy','false');more.disabled = false;}
      }
    }
    review(review) {
      if (!Number.isInteger(review.rating) || review.rating < 1 || review.rating > 5) throw new Error('review');
      const date = new Date(review.submittedAt);if (!Number.isFinite(date.getTime())) throw new Error('date');
      const article = el('article','ucw-product-reviews__review');
      const row = el('div','ucw-product-reviews__review-meta');
      const stars = el('span','ucw-product-reviews__rating','★'.repeat(review.rating)+'☆'.repeat(5-review.rating));
      stars.setAttribute('aria-label',review.rating+' van 5 sterren');
      const time = el('time','ucw-product-reviews__meta',new Intl.DateTimeFormat('nl-NL').format(date));time.dateTime = date.toISOString();
      row.append(stars,time);article.append(row);
      const title = typeof review.title === 'string' ? review.title.trim() : '';
      const comment = typeof review.comment === 'string' ? review.comment.trim() : '';
      if (title) article.append(el('h3','',title));
      if (comment && comment !== title) article.append(el('p','',comment));
      article.append(el('p','ucw-product-reviews__author',typeof review.author === 'string' && review.author.trim() ? review.author : 'Anonieme klant'));
      const link = el('a','ucw-product-reviews__product-link');link.href = this.productUrl(review.productHandle);
      const copy = el('span','ucw-product-reviews__product-copy');
      copy.append(el('span','ucw-product-reviews__product-title',review.productTitle));
      if (this.dataset.groupName) copy.append(el('span','ucw-product-reviews__series','Serie '+this.dataset.groupName));
      link.append(this.photo(review.productHandle,36),copy);article.append(link);
      if (typeof review.reply?.comment === 'string' && review.reply.comment.trim()) {
        const reply = el('details','ucw-product-reviews__reply');reply.open = true;
        reply.append(el('summary','','Reactie van UwCartridgeWinkel'),el('p','',review.reply.comment));
        const replyDate = new Date(review.reply.submittedAt);
        if (review.reply.submittedAt && Number.isFinite(replyDate.getTime())) {
          const time = el('time','',new Intl.DateTimeFormat('nl-NL').format(replyDate));time.dateTime = replyDate.toISOString();reply.append(time);
        }
        article.append(reply);
      }
      return article;
    }
  }
  customElements.define('ucw-product-reviews',UcwProductReviews);
})();


